import {
    Broadcast,
    Asset,
    isLiveEvent,
    ASSET_TYPE,
    PageSection,
    getIsPodcastEpisode,
} from '@24i/nxg-sdk-photon';
import { getDurationString, timeDateText } from '@24i/nxg-core-utils';
import { ItemRender } from '../View/types';

export const SEE_ALL_ITEM_ID = 'See-all';

export const getTimeTextForAsset = (
    asset: Asset | Broadcast,
    is12HourClock: boolean,
    showOnlyHours
) => {
    const { start, startsAt, end, endsAt } = asset;
    const startTime = start || startsAt;
    const endTime = end || endsAt;
    if (!startTime || !endTime) return undefined;
    const startDate = new Date(start || (startsAt || 0) * 1000);
    const endDate = new Date(end || (endsAt || 0) * 1000);

    return timeDateText(
        startDate,
        endDate,
        is12HourClock,
        isLiveEvent(asset),
        !!asset.isLive,
        true,
        showOnlyHours
    );
};

export const getSubtitle = (item, subtitle, t): string | undefined => {
    const { seasonNumber, episodeNumber, podcastName, duration, type, genres } = item;
    const isPodcastEpisode = getIsPodcastEpisode(item);
    const isSeriesEpisode = !!(
        type === ASSET_TYPE.EPISODE &&
        !isPodcastEpisode &&
        seasonNumber &&
        episodeNumber
    );
    const isClip = type === ASSET_TYPE.CLIP;
    const isChannel = type === ASSET_TYPE.CHANNEL;
    const genresStr = genres?.reduce(
        (acc, genre) => (acc === '' ? `${genre.label}` : `${acc}, ${genre.label}`),
        ''
    );

    switch (true) {
        case isPodcastEpisode:
            return podcastName;

        case isSeriesEpisode:
            return t('asset.series.episode.number', {
                seasonNumber,
                episodeNumber,
                episodeTitle: item.title,
            });

        case isClip:
            return getDurationString(duration, t) || subtitle;

        case isChannel:
            return genresStr;

        default:
            return subtitle;
    }
};

export const getPackshotImage = (item: ItemRender, section?: PageSection): string => {
    let {
        poster,
        heroPortrait,
        heroLandscape,
        backgroundPortrait,
        still,
        background,
        packshotLandscape,
    } = item;
    const { type, broadcastMetadata } = item;
    // assets of type channel have an extra property with metadata
    // from the broacasted program of the moment
    if (item && type === ASSET_TYPE.CHANNEL && broadcastMetadata) {
        ({
            poster,
            heroPortrait,
            heroLandscape,
            backgroundPortrait,
            still,
            background,
            packshotLandscape,
        } = broadcastMetadata);
    }

    // TO DO: asset of type custom_item have only a background property
    // for both landscape and portrait
    // for now we can only address this here but we need to align
    // the asset property with the other asset in the app
    if (item?.type === ASSET_TYPE.CUSTOM && item.id !== 'See-all') {
        return backgroundPortrait || background || '';
    }

    return section?.display === 'portrait'
        ? poster || heroPortrait || backgroundPortrait || still || ''
        : packshotLandscape || still || heroLandscape || background || '';
};

export const transformItems = (
    initialSection: PageSection,
    itemLimit: number,
    renderShowAllButton?: boolean
): PageSection['items'] => {
    const items = initialSection.items?.slice(0, itemLimit) || [];
    if (initialSection.additionalItems) {
        const { additionalItems } = initialSection;
        additionalItems
            .slice()
            .reverse()
            .forEach(
                (
                    {
                        textPlacement,
                        position,
                        images,
                        target,
                        title: additionalItemTitle,
                        description,
                    },
                    i
                ) => {
                    // According to typing images does not match here.
                    // using as to get going without major changes as
                    // I have so much to do while updating some 3rd party
                    // packages.
                    //
                    // TODO: when you see this, check why images property
                    // does not work. According to typeguards (which I'm
                    // propably misunderstanding) types for both are the
                    // same.
                    const item = {
                        title: additionalItemTitle,
                        subtitle: description,
                        background: images?.[0].url,
                        images,
                        textPlacement,
                        type: ASSET_TYPE.CUSTOM,
                        target,
                        id: additionalItemTitle || i.toString(),
                    } as Exclude<PageSection['items'], void>[number];
                    if (position === 'start') items.unshift(item);
                    if (position === 'end') items.push(item);
                }
            );
    }
    if (renderShowAllButton)
        items.push({
            id: SEE_ALL_ITEM_ID,
            type: ASSET_TYPE.CUSTOM,
        });

    return items;
};
